import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FaCommentDots, FaLightbulb, FaBug, FaQuestionCircle, FaThumbsUp, FaEllipsisH,
  FaInfoCircle, FaCheckCircle
} from 'react-icons/fa';
import axios from '../axiosInstance';
import { UserContext } from '../UserContext';
import '../Pages.css';
import './FeedbackButton.css';

// Round Feedback button (bottom right of every signed-in page) and a short
// form: optional rating, what it's about, the message. Saved and emailed to
// PIP Intel; staff read it under the Feedback menu.

export const FEEDBACK_RATINGS = [
  { v: 1, face: '😞', label: 'Very poor' },
  { v: 2, face: '🙁', label: 'Poor' },
  { v: 3, face: '😐', label: 'Okay' },
  { v: 4, face: '🙂', label: 'Good' },
  { v: 5, face: '😄', label: 'Excellent' }
];

export const FEEDBACK_CATEGORIES = [
  { v: 'suggestion', label: 'Suggestion', icon: <FaLightbulb /> },
  { v: 'problem',    label: 'Problem',    icon: <FaBug /> },
  { v: 'question',   label: 'Question',   icon: <FaQuestionCircle /> },
  { v: 'compliment', label: 'Compliment', icon: <FaThumbsUp /> },
  { v: 'other',      label: 'Other',      icon: <FaEllipsisH /> }
];

const MAX_LEN = 2000;
const PLACEHOLDER = {
  suggestion: 'What would make PIP Intel better for you?',
  problem:    'What happened, and what did you expect to happen?',
  question:   'What would you like to know?',
  compliment: 'What do you like?',
  other:      'Tell us what is on your mind…'
};

// `publicMode`: login page — the visitor isn't signed in, so they give their
// name and email (sent to POST /feedback/public).
function FeedbackForm({ user, page, onClose, publicMode }) {
  const [who, setWho] = useState({ name: '', email: '', organisation: '', website: '' });
  const [rating, setRating] = useState(null);
  const [category, setCategory] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [contactOk, setContactOk] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const textRef = useRef(null);

  useEffect(() => { if (textRef.current) textRef.current.focus(); }, []);

  // Close automatically a moment after the thank-you.
  useEffect(() => {
    if (!sent) return undefined;
    const t = setTimeout(onClose, 2500);
    return () => clearTimeout(t);
  }, [sent, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (publicMode) {
      if (who.name.trim().length < 2) { setError('Please enter your name.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(who.email.trim())) { setError('Please enter a valid email address.'); return; }
    }
    if (message.trim().length < 3) {
      setError('Tell us a little more (at least 3 characters).');
      if (textRef.current) textRef.current.focus();
      return;
    }
    setError(null);
    setSending(true);
    try {
      const body = { category, rating, message: message.trim(), page, contact_ok: contactOk };
      if (publicMode) {
        await axios.post('/feedback/public', {
          ...body,
          name: who.name.trim(),
          email: who.email.trim(),
          organisation: who.organisation.trim() || null,
          website: who.website || null
        });
      } else {
        await axios.post('/feedback', body);
      }
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not send your feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="feedback-thanks" role="status">
        <FaCheckCircle className="feedback-thanks__icon" />
        <h3>Thank you!</h3>
        <p>Your feedback has been sent to the PIP Intel team.</p>
      </div>
    );
  }

  const name = user ? ([user.first_name, user.last_name].filter(Boolean).join(' ') || user.email) : '';
  const org = user ? (user.organisation_name || user.organization_name) : '';
  const setWhoField = (k) => (e) => { setWho((w) => ({ ...w, [k]: e.target.value })); if (error) setError(null); };

  return (
    <form onSubmit={submit}>
      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {publicMode && (
        <div className="feedback-block feedback-who">
          <input
            className="feedback-input"
            placeholder="Your name *"
            aria-label="Your name"
            autoComplete="name"
            value={who.name}
            onChange={setWhoField('name')}
          />
          <input
            className="feedback-input"
            type="email"
            placeholder="Your email *"
            aria-label="Your email"
            autoComplete="email"
            value={who.email}
            onChange={setWhoField('email')}
          />
          <input
            className="feedback-input feedback-who__full"
            placeholder="Organisation (optional)"
            aria-label="Organisation"
            autoComplete="organization"
            value={who.organisation}
            onChange={setWhoField('organisation')}
          />
          {/* Honeypot: hidden from people, filled by bots */}
          <input
            className="feedback-hp"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={who.website}
            onChange={setWhoField('website')}
          />
        </div>
      )}

      <div className="feedback-block">
        <p className="feedback-question">How is your experience with PIP Intel? <span style={{ fontWeight: 400, color: 'var(--text-secondary,#607d8b)' }}>(optional)</span></p>
        <div className="feedback-rating" role="radiogroup" aria-label="Rating">
          {FEEDBACK_RATINGS.map((r) => (
            <button
              key={r.v}
              type="button"
              role="radio"
              aria-checked={rating === r.v}
              className={`feedback-rating__btn${rating === r.v ? ' feedback-rating__btn--active' : ''}`}
              onClick={() => setRating(rating === r.v ? null : r.v)}
              title={r.label}
            >
              <span aria-hidden="true">{r.face}</span>
              <span>{r.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="feedback-block">
        <p className="feedback-question">What is it about?</p>
        <div className="feedback-chips" role="radiogroup" aria-label="Feedback type">
          {FEEDBACK_CATEGORIES.map((c) => (
            <button
              key={c.v}
              type="button"
              role="radio"
              aria-checked={category === c.v}
              className={`feedback-chip${category === c.v ? ' feedback-chip--active' : ''}`}
              onClick={() => setCategory(c.v)}
            >
              {c.icon} {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="feedback-block">
        <label className="feedback-question" htmlFor="feedback-message" style={{ display: 'block' }}>
          Your feedback <span style={{ color: '#d32f2f' }}>*</span>
        </label>
        <textarea
          id="feedback-message"
          ref={textRef}
          className="feedback-textarea"
          value={message}
          maxLength={MAX_LEN}
          placeholder={PLACEHOLDER[category]}
          onChange={(e) => { setMessage(e.target.value); if (error) setError(null); }}
        />
        <div className="feedback-counter">{message.length}/{MAX_LEN}</div>
      </div>

      <label className="feedback-contact">
        <input type="checkbox" checked={contactOk} onChange={(e) => setContactOk(e.target.checked)} />
        You may contact me about this feedback
      </label>
      {!publicMode && (
        <div className="feedback-sender">
          Sent as {name}{org ? ` · ${org}` : ''}. The screen you are on is included.
        </div>
      )}

      <div className="form-actions" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button type="button" className="btn-cancel" onClick={onClose} disabled={sending}>Cancel</button>
        <button type="submit" className={`export-button${sending ? ' is-loading' : ''}`} disabled={sending}>
          {sending ? 'Sending…' : 'Send Feedback'}
        </button>
      </div>
    </form>
  );
}

export default function FeedbackButton({ publicMode = false }) {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!user && !publicMode) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          className="feedback-fab"
          onClick={() => setOpen(true)}
          title="Send us feedback"
          aria-label="Send feedback"
        >
          <FaCommentDots />
          <span>Feedback</span>
        </button>
      )}

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div
            className="modal-content feedback-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="feedback-title">Send Feedback</h2>
              <button className="modal-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <FeedbackForm user={publicMode ? null : user} page={location.pathname} onClose={close} publicMode={publicMode} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
